import React, { useEffect, useRef, useState } from 'react';
import {
  getProposal,
  SCRUBBED_PLACEHOLDER,
  type PlanProposal,
} from '../../services/plansChatApi';
import './ProposalCard.css';

interface ProposalCardProps {
  proposalId: number;
  onResolve: (
    proposalId: number,
    decision: 'approve' | 'reject',
  ) => Promise<PlanProposal>;
}

const STATUS_LABELS: Record<string, string> = {
  applied: 'Applied',
  rejected: 'Not applied',
  superseded: 'Replaced by a newer suggestion',
  conflicted: 'Not applied — the plan changed',
  expired: 'Expired',
};

/**
 * The approve-before-apply surface. The chat envelope carries only a
 * reference; the card body comes from hydration, so the server's retention
 * scrub has exactly one copy to null — a scrubbed proposal renders the
 * verbatim state-neutral placeholder.
 */

/** The change body: structured rows when the server sent them (Sep-3 field
 *  round: the joined mono blob read as raw text), the text card as the
 *  fallback for proposals created before the column existed. `head`/`extras`
 *  are CONTENT and render as text nodes only — the server quotes them as
 *  data, and this sink contract is the guarantee. */
const ChangeBody: React.FC<{ proposal: PlanProposal }> = ({ proposal }) => {
  if (proposal.card_rows?.length) {
    return (
      <ul className="proposal-card__rows">
        {proposal.card_rows.map((row, i) => (
          <li key={i} className="proposal-card__row">
            <span className="proposal-card__marker" aria-hidden="true">
              {row.marker}
            </span>
            <span className="proposal-card__row-body">
              <span className="proposal-card__head">{row.head}</span>
              {row.extras.map((extra, j) => (
                <span key={j} className="proposal-card__extra">{extra}</span>
              ))}
            </span>
          </li>
        ))}
      </ul>
    );
  }
  if (proposal.card) {
    return <pre className="proposal-card__lines">{proposal.card}</pre>;
  }
  return null;
};

const ProposalCard: React.FC<ProposalCardProps> = ({ proposalId, onResolve }) => {
  const [proposal, setProposal] = useState<PlanProposal | null>(null);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [resolveError, setResolveError] = useState(false);
  const busyRef = useRef(false);
  const cardMountedRef = useRef(true);

  useEffect(() => {
    cardMountedRef.current = true;
    return () => { cardMountedRef.current = false; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    getProposal(proposalId)
      .then(p => { if (!cancelled) setProposal(p); })
      .catch(() => { if (!cancelled) setLoadError(true); });
    return () => { cancelled = true; };
  }, [proposalId]);

  const decide = async (decision: 'approve' | 'reject') => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setResolveError(false);
    try {
      const result = await onResolve(proposalId, decision);
      if (cardMountedRef.current) setProposal(result);
    } catch {
      // A transient failure must not eat the decision surface — the card
      // stays pending with an inline, retryable error (internal review).
      if (cardMountedRef.current) setResolveError(true);
    } finally {
      busyRef.current = false;
      if (cardMountedRef.current) setBusy(false);
    }
  };

  if (loadError) {
    return (
      <div className="proposal-card">
        <div className="proposal-card__error">
          Couldn&apos;t load this suggestion. Reopen the chat to try again.
        </div>
      </div>
    );
  }
  if (!proposal) return null;

  if (proposal.status !== 'pending') {
    return (
      <div className="proposal-card">
        <div className="proposal-card__label">Suggested change</div>
        {proposal.content_scrubbed ? (
          <div className="proposal-card__status">{SCRUBBED_PLACEHOLDER}</div>
        ) : (
          <>
            <ChangeBody proposal={proposal} />
            <div className="proposal-card__status" role="status">
              {STATUS_LABELS[proposal.status] || 'Resolved'}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="proposal-card">
      <div className="proposal-card__label">Suggested change — approve?</div>
      <ChangeBody proposal={proposal} />
      {resolveError ? (
        <div className="proposal-card__error" role="status">
          That didn&apos;t go through. Try again.
        </div>
      ) : null}
      <div className="proposal-card__actions">
        <button
          type="button"
          className="proposal-card__btn proposal-card__btn--primary"
          disabled={busy}
          onClick={() => { void decide('approve'); }}
        >
          Apply changes
        </button>
        <button
          type="button"
          className="proposal-card__btn proposal-card__btn--secondary"
          disabled={busy}
          onClick={() => { void decide('reject'); }}
        >
          Keep plan as is
        </button>
      </div>
    </div>
  );
};

export default ProposalCard;
